/**
 * 挂牌模块：发布挂牌 / 我的挂牌 / 下架 / 再上架
 * ★ 架构 §9-2：后端 /listings 可能未实现 → USE_MOCK.listing 开关 + 失败自动降级 mock
 */
import { get, post, put } from './http';
import { canFallbackToMock } from './errors';
import { normalizeCattle } from './cattle';
import {
  mockCreateListing,
  mockListMyListings,
  mockUpdateListingStatus,
  USE_MOCK,
} from './mock';
import type { CreateListingParams, Listing } from '@/types';

/** 发布挂牌（提交审核） */
export async function createListing(params: CreateListingParams): Promise<{ id: number; status: number }> {
  if (USE_MOCK.listing) return mockCreateListing(params);
  try {
    return await post<{ id: number; status: number }>('/listings', params);
  } catch (e) {
    if (canFallbackToMock(e)) return mockCreateListing(params);
    throw e;
  }
}

/** 我的挂牌列表 */
export async function listMyListings(): Promise<Listing[]> {
  if (USE_MOCK.listing) return mockListMyListings();
  try {
    const data = await get<{ list: Listing[] }>('/listings/mine');
    return (data.list ?? []).map((l) => ({
      ...l,
      cattle: l.cattle ? normalizeCattle(l.cattle) : l.cattle,
    }));
  } catch (e) {
    if (canFallbackToMock(e)) return mockListMyListings();
    throw e;
  }
}

/** 下架挂牌 */
export async function offlineListing(id: number): Promise<{ ok: boolean }> {
  if (USE_MOCK.listing) return mockUpdateListingStatus(id, 2);
  try {
    return await put<{ ok: boolean }>(`/listings/${id}/offline`);
  } catch (e) {
    if (canFallbackToMock(e)) return mockUpdateListingStatus(id, 2);
    throw e;
  }
}

/** 再上架挂牌 */
export async function onlineListing(id: number): Promise<{ ok: boolean }> {
  if (USE_MOCK.listing) return mockUpdateListingStatus(id, 1);
  try {
    return await put<{ ok: boolean }>(`/listings/${id}/online`);
  } catch (e) {
    if (canFallbackToMock(e)) return mockUpdateListingStatus(id, 1);
    throw e;
  }
}

/**
 * 编辑挂牌（更新价格/素材等）。
 * 后端 PUT /listings/:id 复用 CreateListingRequest 完整绑定，
 * cattle_ids/trade_mode/price 均 required，故必须传完整请求体，不能用 Partial。
 */
export async function updateListing(id: number, params: CreateListingParams): Promise<{ ok: boolean }> {
  if (USE_MOCK.listing) return { ok: true };
  try {
    return await put<{ ok: boolean }>(`/listings/${id}`, params);
  } catch (e) {
    if (canFallbackToMock(e)) return { ok: true };
    throw e;
  }
}
